/**
 * The one definition of "what is in this app".
 *
 * Every surface that lists screens reads it and must not drift apart: the
 * desktop sidebar and the mobile drawer (`AppNav`), the cards on the home
 * page, the «previous / next» links under a page (`PageNav`), the mobile top
 * bar and the page header's tint.
 * Before this file the home page WAS the navigation — a hardcoded array in
 * `HomePage.vue` and no menu anywhere else — so adding a screen meant editing
 * one place and hoping.
 *
 * Targets are paths, not route names, because the shell needs prefix matching
 * (which section is the current route in?) and a name gives it nothing to
 * compare. The paths are the ones declared in each module's `routes.ts`.
 */

export interface NavLink {
  /** Absolute path, matching the module's route record. */
  to: string;
  title: string;
  /** One line under the title on the home page. Not shown in the menus. */
  subtitle: string;
  icon: string;
  /**
   * An exercise with answers to check, not a reference page or a tool that only
   * shows forms. The menus mark such a link, the home page lists it among the
   * trainers, and inside its section or group it comes LAST: every run of links
   * reads reference first, practice after.
   */
  practice?: boolean;
}

/**
 * A section's own hue. Literal class strings, not a colour name to
 * interpolate — Tailwind only emits classes it can find written out.
 *
 *   tile   the icon square on the home page and in the sidebar
 *   text   the section icon on its own (page header)
 *   soft   a large tinted surface (the home-page section banner)
 */
export interface NavTone {
  tile: string;
  text: string;
  soft: string;
}

const TONES = {
  blue: { tile: 'bg-blue-100 text-blue-700', text: 'text-blue-600', soft: 'bg-blue-50 ring-blue-200/70' },
  violet: { tile: 'bg-violet-100 text-violet-700', text: 'text-violet-600', soft: 'bg-violet-50 ring-violet-200/70' },
  teal: { tile: 'bg-teal-100 text-teal-700', text: 'text-teal-600', soft: 'bg-teal-50 ring-teal-200/70' },
  orange: { tile: 'bg-orange-100 text-orange-700', text: 'text-orange-600', soft: 'bg-orange-50 ring-orange-200/70' },
  rose: { tile: 'bg-rose-100 text-rose-700', text: 'text-rose-600', soft: 'bg-rose-50 ring-rose-200/70' },
  emerald: { tile: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-600', soft: 'bg-emerald-50 ring-emerald-200/70' },
} satisfies Record<string, NavTone>;

/**
 * A topic inside a section — the second level of the table of contents.
 * «Глаголы» is three tenses, each with its own table and exercises; one flat
 * list of eight screens would have to spell the tense out in every label.
 * Under a caption a link is named for its place there — «Таблица», «Тренажёр» —
 * so two groups may hold the same title, and no two links of ONE group may.
 *
 * A group is a HEADING, not a screen: it has no path of its own and nothing
 * navigates to it. Every surface prints its title above (or beside) the links
 * it holds.
 */
export interface NavGroup {
  key: string;
  title: string;
  links: NavLink[];
}

interface NavSectionBase {
  key: string;
  /** The section's hue — navigation and home page only, never grammar. */
  tone: NavTone;
  /** Menu label and home-page heading. */
  title: string;
  /** Home-page subtitle. Says what the section covers in Czech terms. */
  description: string;
  icon: string;
}

/**
 * A section lists its screens either flat (`links`) or by topic (`groups`) —
 * never both. The union is what makes the second level impossible to forget:
 * `section.links` does not type-check on its own, so a surface has to go
 * through `sectionBlocks` (to draw the level) or `sectionLinks` (to ignore it),
 * and neither can silently drop the screens of a grouped section.
 */
export type NavSection = NavSectionBase & (
  | { links: NavLink[]; groups?: undefined }
  | { groups: NavGroup[]; links?: undefined }
);

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'declensions',
    tone: TONES.blue,
    title: 'Склонения',
    description: 'Skloňování podstatných jmen — падежи, образцы, исключения',
    icon: 'i-lucide-layers',
    links: [
      {
        to: '/declensions/theory',
        title: 'Теория и исключения',
        subtitle: 'Падежи, беглое «e», чередования согласных, особые слова',
        icon: 'i-lucide-book-open',
      },
      {
        to: '/declensions/table',
        title: 'Интерактивная таблица',
        subtitle: 'Шпаргалка: формы всех образцов по родам',
        icon: 'i-lucide-table-2',
      },
      {
        to: '/declensions/trainer',
        title: 'Тренажёр',
        subtitle: 'Раунд из 10 вопросов — вводи правильные формы',
        icon: 'i-lucide-dumbbell',
        practice: true,
      },
    ],
  },
  {
    key: 'adjectives',
    tone: TONES.violet,
    title: 'Прилагательные',
    description: 'Skloňování přídavných jmen — мягкие и твёрдые образцы',
    icon: 'i-lucide-palette',
    links: [
      {
        to: '/adjectives/table',
        title: 'Интерактивная таблица',
        subtitle: 'Měkká (další) и tvrdá (malý) по родам и числам',
        icon: 'i-lucide-table-2',
      },
      {
        to: '/adjectives/comparatives',
        title: 'Степени сравнения',
        subtitle: 'Stupňování — pozitiv · komparativ · superlativ',
        icon: 'i-lucide-bar-chart-3',
      },
      {
        to: '/adjectives/possessives',
        title: 'Притяжательные',
        subtitle: 'Přivlastňovací — Tatínkův / Maminčin по родам и числам',
        icon: 'i-lucide-user-check',
      },
    ],
  },
  {
    key: 'pronouns',
    tone: TONES.teal,
    title: 'Местоимения',
    description: 'Zájmena — разряды местоимений и склонение личных',
    icon: 'i-lucide-message-circle',
    links: [
      {
        to: '/pronouns/theory',
        title: 'Разряды местоимений',
        subtitle: 'Osobní, tázací, ukazovací… — 8 разрядов со словами',
        icon: 'i-lucide-book-open',
      },
      {
        to: '/pronouns/personal',
        title: 'Склонение личных',
        subtitle: 'Já, ty, on… по всем падежам ед. и мн. числа',
        icon: 'i-lucide-table-2',
      },
      {
        to: '/pronouns/possessives',
        title: 'Склонение притяжательных',
        subtitle: 'Můj, tvůj, náš, váš, její, jeho, jejich по родам и падежам',
        icon: 'i-lucide-hand',
      },
      {
        to: '/pronouns/demonstratives',
        title: 'Склонение указательных',
        subtitle: 'Ten / tento по родам, числам и падежам',
        icon: 'i-lucide-locate-fixed',
      },
      {
        to: '/pronouns/relative',
        title: 'Склонение относительных',
        subtitle: 'Který, jaký по родам, числам и падежам · jenž',
        icon: 'i-lucide-link',
      },
      {
        to: '/pronouns/indefinite',
        title: 'Склонение неопределённых',
        subtitle: 'Někdo, něco, jakýsi… по падежам',
        icon: 'i-lucide-circle-dashed',
      },
      {
        to: '/pronouns/negative',
        title: 'Склонение отрицательных',
        subtitle: 'Nikdo, nic, nijaký, ničí, žádný по падежам',
        icon: 'i-lucide-ban',
      },
      {
        to: '/pronouns/reflexive',
        title: 'Возвратные se / si',
        subtitle: 'Sebe, sobě, sebou — аналог -ся / -сь',
        icon: 'i-lucide-rotate-ccw',
      },
      {
        to: '/pronouns/defining',
        title: 'Склонение определительных',
        subtitle: 'Každý по родам, числам и падежам',
        icon: 'i-lucide-target',
      },
    ],
  },
  {
    key: 'numerals',
    tone: TONES.orange,
    title: 'Числительные',
    description: 'Číslovky — все разряды: základní, řadové, souborové, druhové, násobné',
    icon: 'i-lucide-calculator',
    links: [
      {
        to: '/numerals/types',
        title: 'Разряды числительных',
        subtitle: 'Druhy číslovek — шесть разрядов, вопросы и отличия',
        icon: 'i-lucide-book-open',
      },
      {
        to: '/numerals/table',
        title: 'Интерактивная таблица',
        subtitle: 'Základní: 1–1 000 000 по родам, с падежом существительного',
        icon: 'i-lucide-table-2',
      },
      {
        to: '/numerals/ordinal',
        title: 'Порядковые числительные',
        subtitle: 'Řadové: první…milióntý по родам + составные формы',
        icon: 'i-lucide-list-ordered',
      },
      {
        to: '/numerals/collective',
        title: 'Собирательные числительные',
        subtitle: 'Souborové: jedny, dvoje, troje… — пары и наборы',
        icon: 'i-lucide-boxes',
      },
      {
        to: '/numerals/kinds',
        title: 'Видовые числительные',
        subtitle: 'Druhové: dvojí, trojí — «dvojí význam», два вида',
        icon: 'i-lucide-shapes',
      },
      {
        to: '/numerals/multiplicative',
        title: 'Многократные числительные',
        subtitle: 'Násobné: dvakrát, pětkrát · dvojnásobný mistr světa',
        icon: 'i-lucide-repeat',
      },
      {
        to: '/numerals/declension',
        title: 'Склонение числительных',
        subtitle: '1–31 по падежам — отдельная таблица на каждое число',
        icon: 'i-lucide-grid-3x3',
      },
      {
        to: '/numerals/builder',
        title: 'Собери число',
        subtitle: 'Любое число прописью — dvacet jedna / jedenadvacet',
        icon: 'i-lucide-calculator',
      },
    ],
  },
  {
    key: 'dates',
    tone: TONES.rose,
    title: 'Даты',
    description: 'Datum — месяцы, их склонение и запись дат',
    icon: 'i-lucide-calendar-days',
    links: [
      {
        to: '/dates/months',
        title: 'Склонение месяцев',
        subtitle: 'Все 12 месяцев × 7 падежей — ед. и мн. число, с подписями',
        icon: 'i-lucide-table-2',
      },
      {
        to: '/dates/words',
        title: 'Дата словами',
        subtitle: '19.05.1956 → devatenáctého května tisíc devět set padesát šest',
        icon: 'i-lucide-spell-check',
      },
      {
        to: '/dates/practice',
        title: 'Тренажёр дат',
        subtitle: 'Собери дату по частям: день, месяц, год — с проверкой каждого слота',
        icon: 'i-lucide-dumbbell',
        practice: true,
      },
    ],
  },
  {
    key: 'verbs',
    tone: TONES.emerald,
    title: 'Глаголы',
    description: 'Slovesa — настоящее, прошедшее и будущее время',
    icon: 'i-lucide-zap',
    groups: [
      {
        key: 'present',
        title: 'Настоящее время',
        links: [
          {
            to: '/verbs/table',
            title: 'Таблица спряжения',
            subtitle: 'Přítomný čas: 5 классов, императив и отрицание ne-',
            icon: 'i-lucide-table-2',
          },
          {
            to: '/verbs/irregular',
            title: 'Неправильные глаголы',
            subtitle: 'být, chtít, jíst, jít, mít, moct, vědět, vidět + модальные',
            icon: 'i-lucide-triangle-alert',
          },
          {
            to: '/verbs/analyzer',
            title: 'Определи класс',
            subtitle: 'Вводишь «on dělá» — получаешь класс и всё спряжение',
            icon: 'i-lucide-search',
          },
          {
            to: '/verbs/trainer',
            title: 'Тренажёр: неправильные',
            subtitle: 'Задания 31–47: подставить форму неправильного глагола и увидеть разбор',
            icon: 'i-lucide-dumbbell',
            practice: true,
          },
        ],
      },
      {
        key: 'past',
        title: 'Прошедшее время',
        links: [
          {
            to: '/verbs/past',
            title: 'Таблица и правила',
            subtitle: 'Minulý čas: причастие на -l, род и число, jsem / jsi',
            icon: 'i-lucide-history',
          },
        ],
      },
      {
        key: 'future',
        title: 'Будущее время',
        links: [
          {
            to: '/verbs/future/theory',
            title: 'Теория',
            subtitle: 'Budoucí čas: udělám и budu dělat — вид глагола решает',
            icon: 'i-lucide-book-open',
          },
          {
            to: '/verbs/future/table',
            title: 'Таблица',
            subtitle: 'Будущее любого глагола словаря, с отрицанием и видовой парой',
            icon: 'i-lucide-table-2',
          },
          {
            to: '/verbs/future/trainer',
            title: 'Тренажёр',
            subtitle: 'Предложения преподавателя: переделать в будущее время',
            icon: 'i-lucide-dumbbell',
            practice: true,
          },
        ],
      },
      {
        key: 'future-irregular',
        title: 'Будущее: неправильные',
        links: [
          {
            to: '/verbs/future/irregular/theory',
            title: 'Теория',
            subtitle: 'být, chtít, jíst, jít, mít, moct, vědět, vidět: четыре способа образовать будущее',
            icon: 'i-lucide-book-open',
          },
          {
            to: '/verbs/future/irregular/table',
            title: 'Таблица',
            subtitle: 'Восемь глаголов × шесть лиц, с отрицанием и совершенным видом',
            icon: 'i-lucide-table-2',
          },
          {
            to: '/verbs/future/irregular/trainer',
            title: 'Тренажёр',
            subtitle: 'Упражнения 111–123: вписать форму будущего времени в пропуск',
            icon: 'i-lucide-dumbbell',
            practice: true,
          },
        ],
      },
    ],
  },
];

/**
 * A run of links as a surface draws it: headed by a topic, or headless.
 */
export interface NavBlock {
  key: string;
  /** `null` — a flat section: no caption, the list looks as it always did. */
  title: string | null;
  links: NavLink[];
}

/**
 * A section as the runs of links to draw — one headless run for a flat
 * section, one run per topic for a grouped one. Both the menu and the home
 * page render this, so a flat section takes the same path it took before
 * topics existed and the two can never disagree about the second level.
 */
export const sectionBlocks = (section: NavSection): NavBlock[] =>
  (section.groups
    ? section.groups.map((group) => ({ key: group.key, title: group.title, links: group.links }))
    : [{ key: section.key, title: null, links: section.links }]);

/** Every screen of a section in menu order, whichever way it lists them. */
export const sectionLinks = (section: NavSection): NavLink[] =>
  section.groups ? section.groups.flatMap((group) => group.links) : section.links;

/** One screen with everything above it — `group` is `null` in a flat section. */
export interface NavEntry {
  section: NavSection;
  group: NavGroup | null;
  link: NavLink;
}

const entriesOf = (section: NavSection): NavEntry[] =>
  section.groups
    ? section.groups.flatMap((group) => group.links.map((link) => ({ section, group, link })))
    : section.links.map((link) => ({ section, group: null, link }));

/**
 * Every screen of the app in the order of the table of contents — what the
 * «previous / next» links walk and what the home page picks its trainers from.
 */
export const NAV_ENTRIES: NavEntry[] = NAV_SECTIONS.flatMap(entriesOf);

/**
 * Where a screen sits, for a caption above its title: «Глаголы · Будущее
 * время». A screen of a group is named for its place under the group's caption
 * («Таблица», «Тренажёр»), so outside the menu the group is the only thing that
 * says which table or which trainer it is.
 */
export const navTrail = (entry: Pick<NavEntry, 'section' | 'group'>): string =>
  (entry.group ? `${entry.section.title} · ${entry.group.title}` : entry.section.title);

/**
 * A section's first screen — where a link to the section as a whole leads.
 * No section has a landing page of its own: each opens on either its theory or
 * its main table.
 */
export const sectionEntryPath = (section: NavSection): string => sectionLinks(section)[0]?.to ?? '/';

const owns = (linkItem: NavLink, path: string): boolean =>
  path === linkItem.to || path.startsWith(`${linkItem.to}/`);

/**
 * The section, topic and screen a path belongs to — the mobile header's title,
 * the page header's tint, the open group of the menu.
 *
 * Matched on whole path segments: `/verbs/table` must not claim
 * `/verbs/table-of-something`, and a screen nested under another's path would
 * go to the LONGEST match rather than the first.
 *
 * `null` means "no screen" (the home page, a 404).
 */
export const findNavLink = (path: string): NavEntry | null => {
  let best: NavEntry | null = null;

  for (const entry of NAV_ENTRIES) {
    if (owns(entry.link, path) && entry.link.to.length > (best?.link.to.length ?? 0)) {
      best = entry;
    }
  }

  return best;
};

/**
 * The section the current route belongs to. `null` means "no section" — the
 * menus then open every group: a collapsed menu on the landing screen hides
 * the whole app from someone who has just arrived.
 */
export const activeSectionKey = (path: string): string | null => findNavLink(path)?.section.key ?? null;
